﻿import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { OperationCode } from 'src/app/models/enum/operation-code';
import { transformDateYYYYMMDD } from 'src/assets/functions/format-date';
import { ApsPermitedScreen } from '../../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../../models/constants/constants-ford-protect';
import { Patterns } from '../../../models/constants/patterns';
import { StatusProduct } from '../../../models/enum/status-product';
import { ApsPermissionService } from '../../../services/aps-permission/aps-permission.service';
import { ProductService } from '../../../services/product/product.service';
import { RateCurveService } from '../../../services/rate-curve/rate-curve.service';
import { RateCurve } from '../../../models/rate-curve/rate-curve';
import { ProductCreateUpdateDto } from 'src/app/models/product/product-create-update';

type ValueChangeEvent<T> = { value: T };
type EditableOption = { label: string; value: string | null };

const NULO_VALUE = '__NULO__';

@Component({
	selector: 'app-new-product',
	templateUrl: './new-product.component.html',
	styleUrls: ['./new-product.component.scss'],
	standalone: false
})
export class NewProductComponent implements OnInit {

	private readonly destroyRef = inject(DestroyRef);
	private readonly ctaIdleDelayMs = 1500;
	private readonly focusOrder: string[] = [
		'optionCountry',
		'optionStatus',
		'inputProductName',
		'inputCode',
		'inputVisits',
		'inputMonths',
		'inputDistance',
		'inputDescriptionProduct',
		'optionAccount',
		'optionActionCode',
		'optionCoverageCode',
		'optionInsuranceCode',
		'inputInvoiceMonths',
		'inputProductDetails',
		'optionRateCurve'
	];

	listCountries = [
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.ARG', value: ConstantsFordProtect.ARG },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.BRA', value: ConstantsFordProtect.BRA },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.CHL', value: ConstantsFordProtect.CHL },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.COL', value: ConstantsFordProtect.COL },
		{ label: 'TRANSLATE.CHOOSE.COUNTRY.VEN', value: ConstantsFordProtect.VEN }
	];

	listStatus = [
		{ label: 'TRANSLATE.ACTIVE', value: StatusProduct.ACTIVE },
		{ label: 'TRANSLATE.INACTIVE', value: StatusProduct.INACTIVE }
	];

	listAccountCodeOWS = [
		{ label: ConstantsFordProtect.ESB, value: ConstantsFordProtect.ESB },
		{ label: ConstantsFordProtect.ESBG, value: ConstantsFordProtect.ESBG },
		{ label: ConstantsFordProtect.ESMP, value: ConstantsFordProtect.ESMP },
		{ label: ConstantsFordProtect.EXTE, value: ConstantsFordProtect.EXTE },
		{ label: ConstantsFordProtect.FLEE, value: ConstantsFordProtect.FLEE },
		{ label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
		{ label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
		{ label: ConstantsFordProtect.NULO, value: NULO_VALUE },
		{ label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
	];

	listActionCodeOWS = [
		{ label: ConstantsFordProtect.A, value: ConstantsFordProtect.A },
		{ label: ConstantsFordProtect.NULO, value: NULO_VALUE },
		{ label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
	];

	listCoverageCodeOWS = [
		{ label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
		{ label: ConstantsFordProtect.ESP, value: ConstantsFordProtect.ESP },
		{ label: ConstantsFordProtect.FLMT, value: ConstantsFordProtect.FLMT },
		{ label: ConstantsFordProtect.FLSN, value: ConstantsFordProtect.FLSN },
		{ label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
		{ label: ConstantsFordProtect.NULO, value: NULO_VALUE },
		{ label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
	];

	listInsuranceCode = [
		{ label: ConstantsFordProtect.AST, value: ConstantsFordProtect.AST },
		{ label: ConstantsFordProtect.MNTC, value: ConstantsFordProtect.MNTC },
		{ label: ConstantsFordProtect.QCM, value: ConstantsFordProtect.QCM },
		{ label: ConstantsFordProtect.REP, value: ConstantsFordProtect.REP },
		{ label: ConstantsFordProtect.WTY, value: ConstantsFordProtect.WTY },
		{ label: ConstantsFordProtect.NULO, value: NULO_VALUE },
		{ label: ConstantsFordProtect.OTHERS, value: ConstantsFordProtect.OTHERS }
	];

	userform!: UntypedFormGroup;
	newProduct: ProductCreateUpdateDto;
	selectedCountry: string | null = null;
	selectedStatus: number | null = null;

	accountCodeUppercase: string = '';
	editableAccount = false;
	messageAccountCode = false;
	placeholderAccountCode: string = '';

	actionCodeUppercase: string = '';
	editableActionCodeOWS = false;
	messageActionCodeOWS = false;
	placeholderActionCodeOWS: string = '';

	coverageUppercase: string = '';
	editableCoverageCodeOWS = false;
	messageCoverageCodeOWS = false;
	placeholderCoverageCodeOWS: string = '';

	insuranceCodeUppercase: string = '';
	editableInsuranceCode = false;
	messageInsuranceCode = false;
	placeholderInsuranceCode: string = '';
	finalOptionalFieldsReviewed = false;

	productCodeUppercase: string = '';
	operation = OperationCode.CAR;
	submitted = false;
	isSaving = false;
	isCtaSuccess = false;
	private debounceTimer: ReturnType<typeof setTimeout> | undefined;

	rateCurves: RateCurve[] = [];
	allRateCurves: RateCurve[] = [];
	rateCurve!: RateCurve;
	placeholderRateCurve = 'TRANSLATE.SELECT';
	selectedRateCurveId: number = 0;

	constructor(
		private fb: UntypedFormBuilder,
		private serviceProduct: ProductService,
		private messageService: MessageService,
		private apsPermissionService: ApsPermissionService,
		private translateService: TranslateService,
		private router: Router,
		private rateCurveService: RateCurveService
	) {

		this.newProduct = new ProductCreateUpdateDto();
	}

	ngOnInit() {

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenViewsPlans);
		this.placeholderAccountCode = 'TRANSLATE.SELECT';
		this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
		this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
		this.placeholderInsuranceCode = 'TRANSLATE.SELECT';

		this.userform = this.fb.group({
			'optionCountry': [null, Validators.required],
			'optionStatus': [null, Validators.required],
			'inputProductName': ['', [Validators.required, Validators.minLength(4), Validators.maxLength(50)]],
			'inputVisits': ['', [Validators.required, Validators.min(1), Validators.max(99), Validators.pattern(Patterns.ONLY_NUMBERS)]],
			'inputMonths': ['', [Validators.required, this.normalizedMinValueValidator(1), Validators.max(999), Validators.maxLength(3), Validators.pattern(Patterns.ONLY_NUMBERS)]],
			'inputDistance': ['', [Validators.required, Validators.maxLength(7), Validators.pattern(Patterns.NUMBERS_POINT), this.normalizedMinValueValidator(1), this.normalizedMaxValueValidator(999999)]],
			'inputCode': ['', [Validators.required, Validators.minLength(1), Validators.maxLength(10)]],
			'inputDescriptionProduct': ['', [Validators.required, Validators.minLength(4), Validators.maxLength(300)]],
			'optionAccount': [undefined, [this.optionalCodeMaxLengthValidator(9, () => this.editableAccount, () => this.listAccountCodeOWS)]],
			'optionActionCode': [undefined, [this.optionalCodeMaxLengthValidator(1, () => this.editableActionCodeOWS, () => this.listActionCodeOWS)]],
			'optionCoverageCode': [undefined, [this.optionalCodeMaxLengthValidator(4, () => this.editableCoverageCodeOWS, () => this.listCoverageCodeOWS)]],
			'optionInsuranceCode': [undefined, [this.insuranceCodeRequiredValidator(4, () => this.editableInsuranceCode, () => this.listInsuranceCode)]],
			'inputInvoiceMonths': [null, [Validators.max(999), Validators.pattern(Patterns.ONLY_NUMBERS)]],
			'inputProductDetails': [null, [Validators.maxLength(1000), Validators.pattern(Patterns.OBSERVATION_DEFINED_CHARACTERS)]],
			'optionRateCurve': [null]
		});

		this.getRateCurvesByCountry(this.apsPermissionService.getUserCountry());

		// Smart debounce with status tracking
		// Listen to form validity changes separately from value changes
		this.userform.statusChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((status) => {
				// Immediately disable button if form becomes invalid
				if (status !== 'VALID') {
					clearTimeout(this.debounceTimer);
					this.finalOptionalFieldsReviewed = false;
					this.isCtaSuccess = false;
				}
			});
	}

	private checkFormCompletion(): void {
		this.isCtaSuccess = this.userform?.valid ?? false;
	}

	private getInputTarget(event: Event): HTMLInputElement | null {
		return event.target instanceof HTMLInputElement ? event.target : null;
	}

	formatNumber(event: Event, maxLength?: number) {
		const input = this.getInputTarget(event);
		if (!input) {
			return '';
		}

		const value = input.value;
		const rawNumericValue = value.replace(/[^0-9]/g, '');
		const valueFormat = typeof maxLength === 'number'
			? rawNumericValue.slice(0, maxLength)
			: rawNumericValue;

		input.value = valueFormat;
		return valueFormat;
	}

	onMonthsInput(event: Event): void {
		this.formatNumber(event, 3);
	}

	onDistanceInput(event: Event): void {
		const formattedValue = this.formatDistanceNumber(event, 6);
		this.userform?.get('inputDistance')?.setValue(formattedValue, { emitEvent: false });
	}

	private formatDistanceNumber(event: Event, maxDigits: number): string {
		const input = this.getInputTarget(event);
		if (!input) {
			return '';
		}

		const rawNumericValue = input.value.replace(/\D/g, '').slice(0, maxDigits);
		const formattedValue = rawNumericValue.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

		input.value = formattedValue;
		return formattedValue;
	}

	private normalizeDigitsOnly(value: string | number | null | undefined): string {
		return (value ?? '').toString().replace(/\D/g, '');
	}

	private normalizedMaxValueValidator(maxValue: number): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const normalizedValue = this.normalizeDigitsOnly(control.value);

			if (normalizedValue === '') {
				return null;
			}

			return Number(normalizedValue) <= maxValue
				? null
				: { max: { max: maxValue, actual: Number(normalizedValue) } };
		};
	}

	private normalizedMinValueValidator(minValue: number): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const normalizedValue = this.normalizeDigitsOnly(control.value);

			if (normalizedValue === '') {
				return null;
			}

			return Number(normalizedValue) >= minValue
				? null
				: { min: { min: minValue, actual: Number(normalizedValue) } };
		};
	}

	buttonReturn() {
		this.router.navigate(['view-plans'], { skipLocationChange: false, replaceUrl: false });
	}

	countryChange(event: ValueChangeEvent<string | null | undefined>) {
		this.selectedCountry = event.value ?? null;
	}

	statusChange(event: ValueChangeEvent<number | null | undefined>) {
		this.selectedStatus = event.value ?? null;
	}

	productCodeOnKeyUp(event: KeyboardEvent) {
		const input = this.getInputTarget(event);
		this.productCodeUppercase = input?.value.toUpperCase() ?? '';
	}

	accountOnChange(event: ValueChangeEvent<string | null>) {

		this.messageAccountCode = false;
		this.accountCodeUppercase = event.value ?? '';

		if (this.accountCodeUppercase === NULO_VALUE) {
			this.editableAccount = false;
			this.placeholderAccountCode = 'TRANSLATE.SELECT';
		} else if (this.accountCodeUppercase === ConstantsFordProtect.OTHERS) {
			this.editableAccount = true;
			this.placeholderAccountCode = 'TRANSLATE.INFORM.THE.OTHER';
			this.userform?.get('optionAccount')?.setValue('', { emitEvent: false });
		} else if (this.editableAccount && !this.isOptionValue(this.listAccountCodeOWS, event.value)) {
			this.accountCodeUppercase = this.normalizeEditableValue(event.value, 9);
			this.userform?.get('optionAccount')?.setValue(this.accountCodeUppercase, { emitEvent: false });
		} else {
			this.editableAccount = false;
			this.placeholderAccountCode = 'TRANSLATE.SELECT';
		}

		this.userform?.get('optionAccount')?.updateValueAndValidity({ emitEvent: false });

	}

	actionCodeOnChange(event: ValueChangeEvent<string | null>) {

		this.messageActionCodeOWS = false;
		this.actionCodeUppercase = event.value ?? '';

		if (this.actionCodeUppercase === NULO_VALUE) {
			this.editableActionCodeOWS = false;
			this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
		} else if (this.actionCodeUppercase === ConstantsFordProtect.OTHERS) {
			this.editableActionCodeOWS = true;
			this.placeholderActionCodeOWS = 'TRANSLATE.INFORM.THE.OTHER';
			this.userform?.get('optionActionCode')?.setValue('', { emitEvent: false });
		} else if (this.editableActionCodeOWS && !this.isOptionValue(this.listActionCodeOWS, event.value)) {
			this.actionCodeUppercase = this.normalizeEditableValue(event.value, 1);
			this.userform?.get('optionActionCode')?.setValue(this.actionCodeUppercase, { emitEvent: false });
		} else {
			this.editableActionCodeOWS = false;
			this.placeholderActionCodeOWS = 'TRANSLATE.SELECT';
		}

		this.userform?.get('optionActionCode')?.updateValueAndValidity({ emitEvent: false });

	}

	coverageCodeOnChange(event: ValueChangeEvent<string | null>) {

		this.messageCoverageCodeOWS = false;
		this.coverageUppercase = event.value ?? '';

		if (this.coverageUppercase === NULO_VALUE) {
			this.editableCoverageCodeOWS = false;
			this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
		} else if (this.coverageUppercase === ConstantsFordProtect.OTHERS) {
			this.editableCoverageCodeOWS = true;
			this.placeholderCoverageCodeOWS = 'TRANSLATE.INFORM.THE.OTHER';
			this.userform?.get('optionCoverageCode')?.setValue('', { emitEvent: false });
		} else if (this.editableCoverageCodeOWS && !this.isOptionValue(this.listCoverageCodeOWS, event.value)) {
			this.coverageUppercase = this.normalizeEditableValue(event.value, 4);
			this.userform?.get('optionCoverageCode')?.setValue(this.coverageUppercase, { emitEvent: false });
		} else {
			this.editableCoverageCodeOWS = false;
			this.placeholderCoverageCodeOWS = 'TRANSLATE.SELECT';
		}

		this.userform?.get('optionCoverageCode')?.updateValueAndValidity({ emitEvent: false });

	}

	insuranceCodeOnChange(event: ValueChangeEvent<string | null>) {
		this.finalOptionalFieldsReviewed = false;
		this.isCtaSuccess = false;
		clearTimeout(this.debounceTimer);

		this.messageInsuranceCode = false;
		this.insuranceCodeUppercase = event.value ?? '';

		if (this.insuranceCodeUppercase === NULO_VALUE) {
			this.editableInsuranceCode = false;
			this.placeholderInsuranceCode = 'TRANSLATE.SELECT';
		} else if (this.insuranceCodeUppercase === ConstantsFordProtect.OTHERS) {
			this.editableInsuranceCode = true;
			this.placeholderInsuranceCode = 'TRANSLATE.INFORM.THE.OTHER';
			this.userform?.get('optionInsuranceCode')?.setValue('', { emitEvent: false });
		} else if (this.editableInsuranceCode && !this.isOptionValue(this.listInsuranceCode, event.value)) {
			this.insuranceCodeUppercase = this.normalizeEditableValue(event.value, 4);
			this.userform?.get('optionInsuranceCode')?.setValue(this.insuranceCodeUppercase, { emitEvent: false });
		} else {
			this.editableInsuranceCode = false;
			this.placeholderInsuranceCode = 'TRANSLATE.SELECT';
		}

		this.userform?.get('optionInsuranceCode')?.updateValueAndValidity({ emitEvent: false });
		this.scheduleCtaAfterIdle();

	}

	onInvoiceMonthsFocus(): void {
		this.finalOptionalFieldsReviewed = false;
		this.isCtaSuccess = false;
		clearTimeout(this.debounceTimer);
	}

	onInvoiceMonthsBlur(): void {
		setTimeout(() => {
			const activeElement = document.activeElement as HTMLElement | null;
			if (activeElement?.id === 'productDetailsNew') {
				return;
			}

			this.scheduleCtaAfterIdle();
		}, 0);
	}

	onInvoiceMonthsInput(event: Event): void {
		this.formatNumber(event);
		this.scheduleCtaAfterIdle();
	}

	onProductDetailsFocus(): void {
		this.finalOptionalFieldsReviewed = false;
		this.isCtaSuccess = false;
		clearTimeout(this.debounceTimer);
	}

	onProductDetailsInput(): void {
		this.scheduleCtaAfterIdle();
	}

	onFinalOptionalFieldsBlur(): void {
		this.scheduleCtaAfterIdle();
	}

	private scheduleCtaAfterIdle(): void {
		this.finalOptionalFieldsReviewed = true;
		clearTimeout(this.debounceTimer);

		if (this.userform?.status !== 'VALID') {
			this.isCtaSuccess = false;
			return;
		}

		this.debounceTimer = setTimeout(() => {
			if (this.userform?.status === 'VALID' && this.finalOptionalFieldsReviewed) {
				this.isCtaSuccess = true;
			}
		}, this.ctaIdleDelayMs);
	}

	private isOptionValue(options: EditableOption[], value: string | null | undefined): boolean {
		return options.some(option => option.value === value);
	}

	private normalizeEditableValue(value: string | null | undefined, maxLength: number): string {
		return (value || '').toString().toUpperCase().trim().slice(0, maxLength);
	}

	private insuranceCodeRequiredValidator(
		maxLength: number,
		isEditableMode: () => boolean,
		getOptions: () => EditableOption[]
	): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;

			// NULO sentinel is valid (will be converted to null on save).
			if (value === NULO_VALUE) {
				return null;
			}

			// Empty/undefined is invalid (Insurance Code is required).
			if (value === undefined || value === null || value === '') {
				return { required: true };
			}

			const normalizedValue = value.toString().toUpperCase().trim();
			const isPredefinedOption = this.isOptionValue(getOptions(), normalizedValue);

			if (isEditableMode() && !isPredefinedOption) {
				if (normalizedValue === '') {
					return { required: true };
				}

				return normalizedValue.length <= maxLength
					? null
					: { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
			}

			return normalizedValue.length <= maxLength
				? null
				: { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
		};
	}

	private optionalCodeMaxLengthValidator(
		maxLength: number,
		isEditableMode: () => boolean,
		getOptions: () => EditableOption[]
	): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;

			// Optional: undefined, null, empty, or NULO sentinel are all valid.
			if (value === undefined || value === null || value === '' || value === NULO_VALUE) {
				return null;
			}

			const normalizedValue = value.toString().toUpperCase().trim();

			if (normalizedValue === '') {
				return null;
			}

			const isPredefinedOption = this.isOptionValue(getOptions(), normalizedValue);

			if (isEditableMode() && !isPredefinedOption) {
				return normalizedValue.length <= maxLength
					? null
					: { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
			}

			return normalizedValue.length <= maxLength
				? null
				: { maxlength: { requiredLength: maxLength, actualLength: normalizedValue.length } };
		};
	}

	private normalizeOptionValue(value: string | null | undefined): string | null {
		// NULO sentinel and null/undefined all map to null for database
		if (value === NULO_VALUE || value === null || value === undefined) {
			return null;
		}

		const normalizedValue = value.toString().toUpperCase().trim();
		return normalizedValue === '' ? null : normalizedValue;
	}

	buttonSave() {

		if (this.isSaving) {
			return;
		}

		this.submitted = true;
		if (!this.userform) {
			return;
		}
		const formValues = this.userform.value;
		this.selectedCountry = formValues.optionCountry;
		this.selectedStatus = formValues.optionStatus;
		this.productCodeUppercase = (formValues.inputCode || '').toUpperCase();
		const normalizedAccountCode = this.normalizeOptionValue(formValues.optionAccount);
		const normalizedActionCode = this.normalizeOptionValue(formValues.optionActionCode);
		const normalizedCoverageCode = this.normalizeOptionValue(formValues.optionCoverageCode);
		const normalizedInsuranceCode = this.normalizeOptionValue(formValues.optionInsuranceCode);
		this.accountCodeUppercase = normalizedAccountCode ?? '';
		this.actionCodeUppercase = normalizedActionCode ?? '';
		this.coverageUppercase = normalizedCoverageCode ?? '';
		this.insuranceCodeUppercase = normalizedInsuranceCode ?? '';

		if (this.selectedCountry === undefined || this.selectedCountry === null) {
			this.userform.get('optionCountry')?.markAsTouched();
			this.selectedCountry = null;
		}
		if (this.selectedStatus === undefined || this.selectedStatus === null) {
			this.userform.get('optionStatus')?.markAsTouched();
			this.selectedStatus = null;
		}
		if (this.userform.get('inputProductName')?.invalid) {
			this.userform.get('inputProductName')?.markAsPending();
		}
		if (this.userform.get('inputVisits')?.invalid) {
			this.userform.get('inputVisits')?.markAsPending();
		}
		if (this.userform.get('inputMonths')?.invalid) {
			this.userform.get('inputMonths')?.markAsPending();
		}
		if (this.userform.get('inputDistance')?.invalid) {
			this.userform.get('inputDistance')?.markAsPending();
		}
		if (this.userform.get('inputCode')?.invalid) {
			this.userform.get('inputCode')?.markAsPending();
		}
		if (this.userform.get('inputDescriptionProduct')?.invalid) {
			this.userform.get('inputDescriptionProduct')?.markAsPending();
		}

		// Reset all error messages (these only show when user selects OUTROS without providing custom value)
		this.messageAccountCode = false;
		this.messageActionCodeOWS = false;
		this.messageCoverageCodeOWS = false;
		this.messageInsuranceCode = false;

		// Check if any field is in OUTROS mode but empty (only this requires error message)
		const hasAccountError = this.accountCodeUppercase === ConstantsFordProtect.OTHERS && this.editableAccount && !this.accountCodeUppercase;
		const hasActionError = this.actionCodeUppercase === ConstantsFordProtect.OTHERS && this.editableActionCodeOWS && !this.actionCodeUppercase;
		const hasCoverageError = this.coverageUppercase === ConstantsFordProtect.OTHERS && this.editableCoverageCodeOWS && !this.coverageUppercase;
		const hasInsuranceError = this.insuranceCodeUppercase === ConstantsFordProtect.OTHERS && this.editableInsuranceCode && !this.insuranceCodeUppercase;

		if (hasAccountError) {
			this.messageAccountCode = true;
		}
		if (hasActionError) {
			this.messageActionCodeOWS = true;
		}
		if (hasCoverageError) {
			this.messageCoverageCodeOWS = true;
		}
		if (hasInsuranceError) {
			this.messageInsuranceCode = true;
		}
		// If any error or form invalid, stop here
		if (hasAccountError || hasActionError || hasCoverageError || hasInsuranceError || this.userform.status !== "VALID") {
			if (hasAccountError) {
				this.focusField('optionAccount');
			} else if (hasActionError) {
				this.focusField('optionActionCode');
			} else if (hasCoverageError) {
				this.focusField('optionCoverageCode');
			} else if (hasInsuranceError) {
				this.focusField('optionInsuranceCode');
			} else {
				this.focusFirstInvalidField();
			}

			if (this.userform.status !== "VALID" && !this.hasProductMinLengthError()) {
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.translateService.instant('TRANSLATE.ERROR.PLEASE.REQUIRED.FIELDS')
				});
			}
			return;
		}

		this.newProduct.country = this.selectedCountry ?? '';
		this.newProduct.statusCode = this.selectedStatus ?? 0;
		this.newProduct.userId = this.apsPermissionService.getUserCsid();
		this.newProduct.name = (formValues.inputProductName || '').trim();
		this.newProduct.description = (formValues.inputDescriptionProduct || '').trim();
		this.newProduct.visit = Number(formValues.inputVisits);
		this.newProduct.months = Number(formValues.inputMonths);
		this.newProduct.kilometers = Number(this.normalizeDigitsOnly(formValues.inputDistance));
		this.newProduct.productCode = this.productCodeUppercase || '';
		this.newProduct.invoiceMonths = (formValues.inputInvoiceMonths !== null && formValues.inputInvoiceMonths !== ''
			? Number(formValues.inputInvoiceMonths)
			: 0);
		this.newProduct.productDetails = formValues.inputProductDetails || null;
		this.newProduct.dateStart = ConstantsFordProtect.DATE_START;
		this.newProduct.dateEnd = ConstantsFordProtect.DATE_END;
		this.newProduct.createDate = transformDateYYYYMMDD(new Date());
		this.newProduct.operation = this.operation;
		this.newProduct.rateCurveId = this.selectedRateCurveId;

		this.newProduct.accountCode = normalizedAccountCode;
		this.newProduct.actionCode = normalizedActionCode;
		this.newProduct.coverageCode = normalizedCoverageCode;
		this.newProduct.insuranceCode = normalizedInsuranceCode || '';

		this.isSaving = true;
		this.serviceProduct.createProduct(this.newProduct)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response: ProductCreateUpdateDto) => {
					this.newProduct = response;
					this.isSaving = false;
					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.SUCCESS.CREATE')
					});
					this.buttonReturn();
				}, error: (err) => {
					this.isSaving = false;
					if (err.status === 404) {
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CREATE.PRODUCT')
						});
					} else if (err.status === 401) {
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
					} else {
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
					}
				}
			});

	}

	hasControlRequiredError(controlName: string): boolean {
		return !!this.submitted && !!this.userform?.get(controlName)?.hasError('required');
	}

	hasControlGreaterThanZeroError(controlName: string): boolean {
		return !!this.submitted && !!this.userform?.get(controlName)?.hasError('min');
	}

	hasControlMinLengthError(controlName: string): boolean {
		return !!this.submitted && !!this.userform?.get(controlName)?.hasError('minlength');
	}

	private hasProductMinLengthError(): boolean {
		return this.hasControlMinLengthError('inputProductName')
			|| this.hasControlMinLengthError('inputDescriptionProduct');
	}

	private focusFirstInvalidField(): void {
		if (!this.userform) {
			return;
		}

		for (const controlName of this.focusOrder) {
			const control = this.userform.get(controlName);
			if (!control || control.disabled) {
				continue;
			}

			if (control.invalid || control.pending) {
				this.focusField(controlName);
				return;
			}
		}
	}

	private focusField(controlName: string): void {
		const selectorByControl: Record<string, string> = {
			optionCountry: '#countryNew',
			optionStatus: '#statusNew',
			inputProductName: '#productNameNew',
			inputCode: '#productCodeNew',
			inputVisits: '#visitNew',
			inputMonths: '#monthsNew',
			inputDistance: '#distanceNew',
			inputDescriptionProduct: '#productDescriptionNew',
			optionAccount: '#accountNew',
			optionActionCode: '#actionCodeNew',
			optionCoverageCode: '#coverageCodeNew',
			optionInsuranceCode: '#insuranceCodeNew',
			inputInvoiceMonths: '#invoiceMonthsNew',
			inputProductDetails: '#productDetailsNew'
		};

		const selector = selectorByControl[controlName];
		if (!selector) {
			return;
		}

		setTimeout(() => {
			const host = document.querySelector(selector) as HTMLElement | null;
			if (!host) {
				return;
			}

			const focusableChild = host.querySelector('input, textarea, .p-dropdown-label, .p-multiselect-label, [tabindex]') as HTMLElement | null;
			const target = focusableChild ?? host;

			target.scrollIntoView({ behavior: 'smooth', block: 'center' });
			target.focus();
		}, 0);
	}

	private getRateCurvesByCountry(country: string) {
	
		this.rateCurveService.getRateCurvesByCountry(country)
		  .pipe(takeUntilDestroyed(this.destroyRef))
		  .subscribe({
			next: (response: RateCurve[]) => {
	
			  this.rateCurves = response;
			  this.allRateCurves = [...response];  // Backup original data for filtering
	
			  console.log('rateCurves:', this.rateCurves);

			  if (this.rateCurves.length > 0) {

				this.rateCurve = new RateCurve();
				this.selectedRateCurveId = 0;
				this.userform.get('optionRateCurve')?.setValue(null, { emitEvent: false });
				this.placeholderRateCurve = 'TRANSLATE.SELECT';
	
			  } else {
				this.rateCurve = new RateCurve();
				this.placeholderRateCurve = 'TRANSLATE.NO.RESULT.FOUND';
			  }
	
			}, error: (err) => {

			  this.placeholderRateCurve = 'TRANSLATE.NO.RESULT.FOUND';
			  this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.PRODUCTS.NOT.FOUND')
			  });
			}
		  });
	  }

	rateCurveOnChange(event: any) {

		const selectedId = event.value;
		this.selectedRateCurveId = selectedId;

		console.log('rate escolhido: ' + this.selectedRateCurveId)
		this.rateCurve = this.rateCurves.find(rc => rc.rateId === selectedId) || new RateCurve();
	}


}
